<template>
  <div class="station-screen">
    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Header Section -->
      <div class="header-section">
        <div class="logo-container">
          <img src="/images/MealWaveAsAtext.png" alt="MealWave" class="logo-img" />
        </div>
        <div class="restaurant-badge">
          <span class="restaurant-name">{{ selectedRestaurantName }}</span>
        </div>
        <p class="page-subtitle">{{ language === 'ar' ? 'اختر محطة العمل' : 'Station auswählen' }}</p>
      </div>

      <!-- Stations Container -->
      <div class="stations-section">
        <!-- Loading State -->
        <div v-if="loading" class="state-container">
          <div class="loader-ring">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <span class="state-text">{{ language === 'ar' ? 'جاري التحميل...' : 'Stationen werden geladen...' }}</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="!activeStations || Object.keys(activeStations).length === 0" class="state-container">
          <div class="empty-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" class="empty-icon">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
              <path d="M8 21h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M12 17v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M8 10h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </div>
          <span class="state-text">{{ language === 'ar' ? 'لا توجد محطات متاحة' : 'Keine Stationen verfügbar' }}</span>
        </div>

        <!-- Station Cards -->
        <div v-else class="stations-grid">
          <div
            v-for="(station, id) in activeStations"
            :key="id"
            class="station-card"
            :class="`theme-${getStationTheme(station)}`"
            @click="selectStation(id, station)"
          >
            <div class="card-glow"></div>
            <div class="card-content">
              <div class="station-icon-container">
                <i :class="getStationIcon(station)"></i>
              </div>
              <div class="station-info">
                <h3 class="station-name">{{ getStationDisplayName(station, id) }}</h3>
                <span v-if="station.destination || station.letter" class="station-code">
                  Code: {{ station.destination || station.letter }}
                </span>
              </div>
              <div class="select-arrow">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="bottom-section">
        <button class="logout-btn" @click="logout">
          <svg viewBox="0 0 24 24" fill="none" class="logout-icon">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ language === 'ar' ? 'تسجيل الخروج' : 'Abmelden' }}</span>
        </button>
        <p class="footer-text">© 2026 MealWave</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'StationSelectionView',
  data() {
    return {
      loading: true,
      stations: null
    };
  },
  computed: {
    ...mapState(['selectedRestaurantId', 'selectedRestaurantName', 'language']),
    activeStations() {
      if (!this.stations) return {};
      return Object.fromEntries(
        Object.entries(this.stations).filter(([, station]) => {
          // Filter out inactive stations
          if (station.active === false) return false;
          // Filter out stations with code "O" (they don't need preparation)
          if (station.destination === 'O' || station.letter === 'O') return false;
          return true;
        })
      );
    }
  },
  mounted() {
    if (!this.selectedRestaurantId) {
      this.$router.push('/');
      return;
    }
    this.loadStations();
  },
  methods: {
    ...mapActions(['setSelectedStation']),
    async loadStations() {
      this.loading = true;
      try {
        const { ref, get } = await import('firebase/database');
        const db = this.$firebase.database();
        const stationsRef = ref(db, `restaurants/${this.selectedRestaurantId}/interfaces`);
        const snapshot = await get(stationsRef);

        this.stations = snapshot.val() || {};
      } catch (error) {
        console.error('Error loading stations:', error);
      } finally {
        this.loading = false;
      }
    },
    selectStation(stationId, station) {
      const destinationCode = station.destination || station.letter || '';
      const stationName = station.name || stationId;

      this.setSelectedStation({
        id: stationId,
        name: stationName,
        destinationCode: destinationCode
      });

      this.$router.push('/orders');
    },
    async logout() {
      // Use the store logout action which handles everything
      await this.$store.dispatch('logout');
      // Force clear any cached auth state
      localStorage.clear();
      // Use replace to prevent back navigation
      this.$router.replace('/login');
    },
    getStationTheme(station) {
      const name = (station.name || '').toLowerCase();
      if (name.includes('drink') || name.includes('مشروب') || name.includes('getränke')) return 'drinks';
      if (name.includes('dessert') || name.includes('حلو')) return 'desserts';
      if (name.includes('grill') || name.includes('شواء')) return 'grill';
      return 'kitchen';
    },
    getStationIcon(station) {
      const name = (station.name || '').toLowerCase();
      if (name.includes('drink') || name.includes('مشروب') || name.includes('getränke')) return 'fas fa-cocktail';
      if (name.includes('dessert') || name.includes('حلو')) return 'fas fa-birthday-cake';
      if (name.includes('grill') || name.includes('شواء')) return 'fas fa-fire';
      if (name.includes('kitchen') || name.includes('مطبخ') || name.includes('küche')) return 'fas fa-utensils';
      return 'fas fa-concierge-bell';
    },
    getStationDisplayName(station, id) {
      const name = (station.name || id || '').toLowerCase();

      if (this.language === 'ar') {
        // Arabic names
        if (name.includes('kitchen') || name.includes('küche')) return 'مطبخ';
        if (name.includes('drink') || name.includes('getränke')) return 'مشروبات';
        if (name.includes('dessert')) return 'حلويات';
        if (name.includes('grill')) return 'شواية';
        // If already in Arabic, return as is
        if (name.includes('مطبخ') || name.includes('مشروب') || name.includes('حلو') || name.includes('شواء')) {
          return station.name || id;
        }
      } else {
        // German names
        if (name.includes('kitchen') || name.includes('مطبخ')) return 'Küche';
        if (name.includes('drink') || name.includes('مشروب')) return 'Getränke';
        if (name.includes('dessert') || name.includes('حلو')) return 'Desserts';
        if (name.includes('grill') || name.includes('شواء')) return 'Grill';
      }

      // Default: return original name
      return station.name || id;
    }
  }
};
</script>

<style scoped>
.station-screen { min-height: 100vh; width: 100%; background: var(--ods-bg); display: flex; align-items: center; justify-content: center; padding: 40px 20px; }
.content-wrapper { width: 100%; max-width: 900px; }
.header-section { text-align: center; margin-bottom: 36px; }
.logo-container { margin-bottom: 18px; display: flex; justify-content: center; }
.logo-img { height: 40px; width: auto; display: block; }
html.dark .logo-img { filter: brightness(1.25); }
.restaurant-badge { display: inline-flex; align-items: center; padding: 8px 16px; background: var(--ods-surface); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); margin-bottom: 12px; }
.restaurant-name { color: var(--ods-ink); font-size: 17px; font-weight: 700; }
.page-subtitle { color: var(--ods-ink-3); font-size: 14px; margin: 0; font-weight: 600; }
.stations-section { min-height: 260px; display: flex; align-items: center; justify-content: center; }
.state-container { display: flex; flex-direction: column; align-items: center; gap: 18px; }
.state-text { color: var(--ods-ink-3); font-size: 15px; font-weight: 600; }
.loader-ring { display: inline-block; position: relative; width: 48px; height: 48px; }
.loader-ring div { box-sizing: border-box; display: block; position: absolute; width: 40px; height: 40px; margin: 4px; border: 3px solid transparent; border-radius: 50%; border-top-color: var(--ods-accent); animation: loaderSpin 1.2s cubic-bezier(.5,0,.5,1) infinite; }
.loader-ring div:nth-child(1) { animation-delay: -.45s; } .loader-ring div:nth-child(2) { animation-delay: -.3s; } .loader-ring div:nth-child(3) { animation-delay: -.15s; }
@keyframes loaderSpin { to { transform: rotate(360deg); } }
.empty-icon-wrapper { width: 72px; height: 72px; background: var(--ods-surface); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); display: flex; align-items: center; justify-content: center; }
.empty-icon { width: 36px; height: 36px; color: var(--ods-ink-3); }
.stations-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; width: 100%; }
.station-card { position: relative; background: var(--ods-surface); border-radius: var(--ods-radius); border: 1px solid var(--ods-line); cursor: pointer; transition: border-color .15s, box-shadow .15s, background .15s; box-shadow: var(--ods-shadow-sm); min-height: 96px; }
.station-card:hover { border-color: var(--ods-accent); box-shadow: var(--ods-shadow); }
.station-card:active { background: var(--ods-surface-2); }
.card-glow { display: none; }
.card-content { position: relative; display: flex; align-items: center; gap: 16px; padding: 20px 22px; }
.station-icon-container { width: 52px; height: 52px; border-radius: var(--ods-radius); display: flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; background: var(--ods-accent-tint); color: var(--ods-accent-ink); }
.theme-drinks .station-icon-container { background: var(--ods-info-tint); color: var(--ods-info-ink); }
.theme-desserts .station-icon-container { background: rgba(236, 72, 153, .14); color: #ec4899; }
.theme-grill .station-icon-container { background: var(--ods-danger-tint); color: var(--ods-danger-ink); }
.station-info { flex: 1; min-width: 0; }
.station-name { color: var(--ods-ink); font-size: 20px; font-weight: 800; margin: 0 0 4px; line-height: 1.2; }
.station-code { color: var(--ods-ink-3); font-size: 12px; font-weight: 700; letter-spacing: .04em; }
.select-arrow { width: 36px; height: 36px; background: var(--ods-surface-2); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); display: flex; align-items: center; justify-content: center; color: var(--ods-ink-3); flex-shrink: 0; transition: all .15s; }
.select-arrow svg { width: 20px; height: 20px; }
.station-card:hover .select-arrow { background: var(--ods-accent); border-color: var(--ods-accent); color: #fff; }
.bottom-section { margin-top: 36px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
.logout-btn { display: flex; align-items: center; gap: 8px; padding: 0 20px; height: 44px; background: var(--ods-surface); border: 1px solid var(--ods-danger); border-radius: var(--ods-radius); color: var(--ods-danger-ink); font-size: 14px; font-weight: 700; font-family: inherit; cursor: pointer; }
.logout-btn:hover { background: var(--ods-danger-tint); }
.logout-icon { width: 18px; height: 18px; }
.footer-text { color: var(--ods-ink-3); font-size: 12px; margin: 0; }
@media (max-width: 768px) { .station-screen { padding: 24px 14px; } .stations-grid { grid-template-columns: 1fr; gap: 12px; } .station-name { font-size: 18px; } }
</style>
